/**
 * MoveTeamModal - Move a team to a different project
 */

import React, { useState } from 'react';

interface MoveTeamModalProps {
  teamId: number;
  teamName: string;
  currentProjectId: number;
  projects: Array<{ id: number; name: string }>;
  onClose: () => void;
  onMove: (projectId: number) => Promise<void>;
}

export const MoveTeamModal: React.FC<MoveTeamModalProps> = ({
  teamName,
  currentProjectId,
  projects,
  onClose,
  onMove,
}) => {
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const options = projects.filter(p => p.id !== currentProjectId);

  const handleConfirm = async () => {
    if (selectedProjectId == null) {
      setError('Select a project');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await onMove(selectedProjectId);
      onClose(); // Parent clears state so modal unmounts
    } catch (err: any) {
      setError(err.message || 'Failed to move team');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="w-[440px] bg-slate-800 border border-gray-700 rounded-xl shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-white font-bold text-lg">Move team to project</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 text-lg">×</button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-gray-400 text-xs mb-1">Team</label>
            <div className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-300 text-sm">
              {teamName}
            </div>
          </div>

          <div>
            <label className="block text-gray-400 text-xs mb-1">Project</label>
            <select
              value={selectedProjectId ?? ''}
              onChange={e => { setSelectedProjectId(e.target.value ? parseInt(e.target.value, 10) : null); setError(''); }}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 text-sm outline-none focus:border-indigo-500 transition-colors"
            >
              <option value="">Select project...</option>
              {options.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            {options.length === 0 && (
              <p className="text-gray-500 text-xs mt-1">No other projects available.</p>
            )}
          </div>

          {error && (
            <p className="text-red-400 text-xs">{error}</p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={submitting || selectedProjectId == null}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {submitting ? 'Moving…' : 'Move'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
